/**
 * Арифметика железа: округление рабочих весов, раскладка блинов, разминка.
 *
 * Инвентарь зашит стандартным набором — решение §14 ТЗ: гриф 20 кг и блины
 * 25/20/15/10/5/2.5/1.25. Настройка появится, когда появится зал с другим
 * набором, а не на упреждение.
 */

export const BAR_KG = 20;
export const PLATES_KG = [25, 20, 15, 10, 5, 2.5, 1.25] as const;

/**
 * Вес грифа по снаряду.
 *
 * `null` — вешать блины некуда: гантель, тренажёр, блок, своё тело. Раскладка
 * блинов для тяги на блоке не подсказка, а бессмыслица, и показывать её нельзя.
 *
 * Гриф Смита у разных машин весит от 6 до 25 кг, угадать нельзя. Взято
 * распространённое значение, но оно ПОКАЗЫВАЕТСЯ рядом с раскладкой: допущение,
 * которое видно, человек поправит сам; допущение, которое спрятано, он унесёт
 * в тренировку.
 */
export const BAR_BY_EQUIPMENT: Record<string, number | null> = {
  barbell: 20,
  ez_bar: 10, // изогнутый гриф легче прямого вдвое
  smith: 15,
  dumbbell: null,
  kettlebell: null,
  machine: null,
  cable: null,
  bodyweight: null,
  band: null,
  other: null,
};

/** Вес грифа для снаряда; `null` — раскладку блинов показывать нечему. */
export const barFor = (equipment: string): number | null =>
  equipment in BAR_BY_EQUIPMENT ? BAR_BY_EQUIPMENT[equipment] : null;

/** Шаг округления рекомендаций. Тот же, что `MROUND(…; 2,5)` в файлах заказчика. */
export const ROUNDING_STEP = 2.5;

/** Минимальный практический вес там, где грифа нет: легче гантелей не бывает. */
export const MIN_STEP_KG = 2.5;

const EPS = 1e-9;

/** Округление к ближайшему кратному шагу — половина всегда вверх. */
export const mround = (value: number, step: number = ROUNDING_STEP): number =>
  Math.round((value / step) * (1 + Number.EPSILON)) * step;

export interface PlateBreakdown {
  /** Блины на одну сторону, от тяжёлых к лёгким. */
  perSide: number[];
  /** Вес, который реально соберётся из этих блинов. */
  achieved: number;
  /** Совпал ли он с запрошенным: нацело собирается не всякий вес. */
  exact: boolean;
}

/**
 * Раскладка блинов на штангу.
 *
 * Жадный перебор от тяжёлых: на стандартном наборе он оптимален, а перебор
 * вариантов ради теоретического случая усложнил бы то, на что смотрят между
 * подходами. `null` — вес меньше грифа, вешать нечего.
 */
export function platesFor(
  weightKg: number,
  barKg: number = BAR_KG,
  plates: readonly number[] = PLATES_KG,
): PlateBreakdown | null {
  if (!Number.isFinite(weightKg) || weightKg < barKg - EPS) return null;

  let left = (weightKg - barKg) / 2;
  const perSide: number[] = [];
  for (const plate of plates) {
    while (left >= plate - EPS) {
      perSide.push(plate);
      left = Math.round((left - plate) * 1000) / 1000;
    }
  }
  const achieved = barKg + 2 * perSide.reduce((sum, p) => sum + p, 0);
  return { perSide, achieved, exact: Math.abs(achieved - weightKg) < EPS };
}

/** «25 + 10 + 2.5» — как это произносят у стойки. */
export const plateLine = (perSide: number[]): string =>
  perSide.length === 0 ? "пустой гриф" : perSide.join(" + ");

export interface WarmupSet {
  weight: number;
  reps: number;
}

/**
 * Ступени разминки: доля рабочего веса и повторы.
 *
 * Схема общепринятая, а не выведенная из файлов заказчика: в его таблицах
 * разминка не расписана вовсе. Поэтому это предложение, которое правится
 * руками, а не предписание.
 */
const WARMUP_STEPS: readonly { fraction: number; reps: number }[] = [
  { fraction: 0, reps: 10 }, // пустой гриф
  { fraction: 0.4, reps: 5 },
  { fraction: 0.6, reps: 3 },
  { fraction: 0.8, reps: 2 },
];

/**
 * Разминочные подходы под рабочий вес.
 *
 * Ступени, которые не легче предыдущей или дотягивают до рабочего веса,
 * выбрасываются: разминаться тем же весом, которым работаешь, бессмысленно.
 */
export function warmupFor(
  workingKg: number,
  barKg: number | null = BAR_KG,
): WarmupSet[] {
  if (!Number.isFinite(workingKg)) return [];

  // Без грифа (гантели, тренажёр) ступени «пустой гриф» не существует, а нижняя
  // граница — минимальный практический вес, а не вес железки.
  const floor = barKg ?? MIN_STEP_KG;
  if (workingKg <= floor + EPS) return [];

  const out: WarmupSet[] = [];
  for (const step of WARMUP_STEPS) {
    if (step.fraction === 0 && barKg === null) continue;
    const weight = Math.max(floor, mround(workingKg * step.fraction));
    if (weight >= workingKg - EPS) continue;
    if (out.length && weight <= out[out.length - 1].weight + EPS) continue;
    out.push({ weight, reps: step.reps });
  }
  return out;
}
